import {
  Caption1,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Menu,
  MenuItemCheckbox,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tab,
  TabList,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerOptionGroup,
  makeStyles,
  tokens,
  type MenuCheckedValueChangeData,
} from "@fluentui/react-components";
import { Tag16Regular } from "@fluentui/react-icons";
import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import { DateField } from "@/components/date-field";
import { FuiButton, FuiInput } from "@/components/fui-control";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import type { TaskLabel } from "@/engine/types";
import { formatDateRange } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { LABEL_STYLE, scopeIcon } from "./task-ui";
import { SCOPE_KIND_LABEL, scopeRef, type ScopeRef, type TaskQueryContext } from "./task-query";

const useStyles = makeStyles({
  control: {
    minHeight: "28px",
  },
  scopeTag: {
    maxWidth: "260px",
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  code: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
    minWidth: "32px",
  },
  name: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dates: {
    marginLeft: "auto",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  tabHeader: {
    position: "sticky",
    top: "-4px",
    marginTop: "-4px",
    marginLeft: "-4px",
    marginRight: "-4px",
    zIndex: 10,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
  },
  pickerList: {
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
  },
  assigneeOption: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    width: "100%",
    padding: "4px 0",
  },
  assigneeText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  assigneeName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: tokens.fontWeightRegular,
    fontSize: tokens.fontSizeBase300,
  },
  assigneeEmail: {
    color: tokens.colorNeutralForeground3,
  },
  assigneeRole: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
  },
});

function SelectedTag({ value, media, children, className, title, dismissLabel }: { value: string; media?: React.ReactElement | null; children: ReactNode; className?: string; title?: string; dismissLabel: string }) {
  return (
    <InteractionTag value={value} shape="rounded" size="small" className={className} title={title}>
      <InteractionTagPrimary hasSecondaryAction media={media}>
        {children}
      </InteractionTagPrimary>
      <InteractionTagSecondary aria-label={`${dismissLabel} 제거`} />
    </InteractionTag>
  );
}

/* ---------------------------------------------------------------- assignee (free text + suggestions) */

export function AssigneePicker({ value, onChange, suggestions, placeholder = "이름 입력 후 ↩", disabled }: { value: string[]; onChange: (next: string[]) => void; suggestions: string[]; placeholder?: string; disabled?: boolean }) {
  const styles = useStyles();
  const [query, setQuery] = useState("");
  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const getWorkload = useCatalogStore((s) => s.getResourceWorkload);

  const byName = useMemo(() => {
    const map = new Map<string, (typeof resources)[string]>();
    for (const r of Object.values(resources)) {
      if (r.isActive !== false) map.set(r.displayName, r);
    }
    return map;
  }, [resources]);

  const options = useMemo(() => {
    const names = new Set<string>([...suggestions, ...byName.keys()]);
    const q = query.trim().toLowerCase();
    return [...names]
      .filter((n) => !value.includes(n))
      .filter((n) => {
        if (!q) return true;
        const r = byName.get(n);
        if (!r) return n.toLowerCase().includes(q);
        return [r.displayName, r.email, r.roleTitle, ...r.skills, teams[r.teamId ?? ""]?.name ?? ""].some((s) => s.toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const ra = byName.get(a);
        const rb = byName.get(b);
        const la = ra ? getWorkload(ra.id).totalAllocationRatio : Number.POSITIVE_INFINITY;
        const lb = rb ? getWorkload(rb.id).totalAllocationRatio : Number.POSITIVE_INFINITY;
        return la - lb || a.localeCompare(b, "ko");
      });
  }, [suggestions, value, query, byName, teams, getWorkload]);

  const add = (name: string) => {
    const n = name.trim();
    if (!n || value.includes(n)) return;
    onChange([...value, n]);
    setQuery("");
  };
  return (
    <TagPicker
      selectedOptions={value}
      onOptionSelect={(_, data) => {
        // a freeform entry has no option: keep whatever was typed
        onChange(data.selectedOptions);
        setQuery("");
      }}
      positioning={{
        position: "below",
        align: "start",
        fallbackPositions: ["above"],
        matchTargetSize: "width",
      }}
      disabled={disabled}
      noPopover={options.length === 0}
      size="medium"
    >
      <TagPickerControl className={styles.control}>
        <TagPickerGroup aria-label="담당자">
          {value.map((n) => (
            <SelectedTag key={n} value={n} media={<PersonAvatar name={n} size={16} tip={false} />} dismissLabel={n}>
              {n}
            </SelectedTag>
          ))}
        </TagPickerGroup>
        <TagPickerInput
          aria-label="담당자 추가"
          placeholder={value.length ? "" : placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && query.trim()) {
              e.preventDefault();
              add(query);
            }
            if (e.key === "Backspace" && !query && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => query.trim() && add(query)}
        />
      </TagPickerControl>
      <TagPickerList className={styles.pickerList} style={{ maxHeight: "360px" }}>
        {options.map((n) => {
          const r = byName.get(n);
          return (
            <TagPickerOption key={n} value={n} text={`${n}${r?.email ? ` (${r.email})` : ""}${r ? ` ${r.roleTitle}` : ""}`}>
              <span className={styles.assigneeOption}>
                <PersonAvatar name={n} size={28} tip={false} />
                <span className={styles.assigneeText}>
                  <span className={styles.assigneeName} title={r?.email ? `${n} (${r.email})` : n}>
                    {n}
                    {r?.email && <span className={styles.assigneeEmail}> ({r.email})</span>}
                  </span>
                  {r?.roleTitle && (
                    <Caption1 className={styles.assigneeRole}>{r.roleTitle}</Caption1>
                  )}
                </span>
              </span>
            </TagPickerOption>
          );
        })}
      </TagPickerList>
    </TagPicker>
  );
}

/* ---------------------------------------------------------------- scope (WBS / Activity) */

type ScopeTab = "activity" | "milestone" | "wbs";

export function ScopePicker({ value, onChange, ctx, placeholder = "WBS 번호나 이름으로 찾기", disabled }: { value: string[]; onChange: (next: string[]) => void; ctx: TaskQueryContext; placeholder?: string; disabled?: boolean }) {
  const styles = useStyles();
  const [tab, setTab] = useState<ScopeTab>("activity");
  const [query, setQuery] = useState("");
  const all = useMemo<ScopeRef[]>(() => {
    const order = ctx.schedule?.order ?? ctx.project.tasks.map((t) => t.id);
    return order.map((id) => scopeRef(id, ctx));
  }, [ctx]);
  const q = query.trim().toLowerCase();
  const unselected = useMemo(() => all.filter((r) => !value.includes(r.id)), [all, value]);
  const matching = useMemo(() => {
    if (!q) return unselected;
    return unselected.filter((r) => `${r.wbs} ${r.name}`.toLowerCase().includes(q));
  }, [unselected, q]);

  const counts = useMemo(
    () => ({
      activity: matching.filter((r) => r.kind === "activity").length,
      milestone: matching.filter((r) => r.kind === "milestone").length,
      wbs: matching.filter((r) => r.kind === "wbs").length,
    }),
    [matching],
  );

  const tabItems = useMemo(() => {
    return matching.filter((r) => r.kind === tab);
  }, [matching, tab]);

  const is10Plus = tabItems.length >= 10;
  const selected = value.map((id) => scopeRef(id, ctx));

  const renderOption = (r: ScopeRef) => (
    <TagPickerOption key={r.id} value={r.id} media={scopeIcon(r.kind)} text={`${r.wbs} ${r.name}`}>
      <span className={styles.optionRow}>
        <span className={styles.code}>{r.wbs}</span>
        <span className={styles.name}>{r.name}</span>
        {r.start && (
          <span className={styles.dates}>
            {formatDateRange(r.start, r.finish, "short")}
          </span>
        )}
      </span>
    </TagPickerOption>
  );

  return (
    <TagPicker
      selectedOptions={value}
      onOptionSelect={(_, data) => {
        if (data.value === "__none") return;
        const valid = data.selectedOptions.filter((x) => x !== "__none");
        onChange(valid);
        setQuery("");
      }}
      positioning={{
        position: "below",
        align: "start",
        fallbackPositions: ["above"],
        matchTargetSize: "width",
      }}
      disabled={disabled}
      size="medium"
    >
      <TagPickerControl className={styles.control}>
        <TagPickerGroup aria-label="연결된 WBS·작업">
          {selected.map((r) => (
            <SelectedTag key={r.id} value={r.id} media={scopeIcon(r.kind)} className={styles.scopeTag} title={`${SCOPE_KIND_LABEL[r.kind]} ${r.wbs} ${r.name}`} dismissLabel={`${r.wbs ? `${r.wbs} ` : ""}${r.name}`.trim()}>
              {r.wbs ? `${r.wbs} ` : ""}
              {r.name}
            </SelectedTag>
          ))}
        </TagPickerGroup>
        <TagPickerInput aria-label="WBS 또는 작업 연결" placeholder={value.length ? "" : placeholder} value={query} onChange={(e) => setQuery(e.target.value)} />
      </TagPickerControl>
      <TagPickerList
        className={styles.pickerList}
        style={{
          height: is10Plus ? "380px" : "auto",
          maxHeight: "380px",
        }}
      >
        <div
          className={styles.tabHeader}
          onMouseDown={(e) => {
            // Prevent input from losing focus when clicking tabs
            e.preventDefault();
          }}
        >
          <TabList
            size="small"
            selectedValue={tab}
            onTabSelect={(_, d) => setTab(d.value as ScopeTab)}
          >
            <Tab value="activity">작업 ({counts.activity})</Tab>
            <Tab value="milestone">마일스톤 ({counts.milestone})</Tab>
            <Tab value="wbs">WBS ({counts.wbs})</Tab>
          </TabList>
        </div>

        {tabItems.length === 0 ? (
          <TagPickerOption value="__none" aria-disabled="true">
            {tab === "milestone"
              ? "일치하는 마일스톤이 없습니다"
              : tab === "wbs"
              ? "일치하는 WBS 단계가 없습니다"
              : "일치하는 작업이 없습니다"}
          </TagPickerOption>
        ) : (
          tabItems.map(renderOption)
        )}
      </TagPickerList>
    </TagPicker>
  );
}

/* ---------------------------------------------------------------- labels */

export function LabelPicker({ value, onChange, labels, trigger }: { value: string[]; onChange: (next: string[]) => void; labels: TaskLabel[]; trigger?: ReactElement }) {
  const checked = { label: value };
  const onChecked = (_: unknown, data: MenuCheckedValueChangeData) => {
    if (data.name === "label") onChange(data.checkedItems);
  };
  return (
    <Menu checkedValues={checked} onCheckedValueChange={onChecked}>
      <MenuTrigger disableButtonEnhancement>
        {trigger ?? (
          <FuiButton appearance="subtle" icon={<Tag16Regular />}>
            라벨
          </FuiButton>
        )}
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {labels.length === 0 && <MenuItemCheckbox name="label" value="__none" disabled>라벨이 없습니다 — 버킷·라벨에서 추가</MenuItemCheckbox>}
          {labels.map((l) => {
            const c = LABEL_STYLE[l.color] ?? LABEL_STYLE.gray;
            return (
              <MenuItemCheckbox key={l.id} name="label" value={l.id} icon={<span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, backgroundColor: c.bg, border: `1px solid ${c.fg}` }} />}>
                {l.name}
              </MenuItemCheckbox>
            );
          })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

/* ---------------------------------------------------------------- date */

export function DateInput({ value, onChange, min, max, placeholder, disabled, ariaLabel }: { value?: string; onChange: (iso: string | undefined) => void; min?: string; max?: string; placeholder?: string; disabled?: boolean; ariaLabel?: string }) {
  return (
    <DateField
      presets
      value={value}
      min={min}
      max={max}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={onChange}
    />
  );
}
